// Universal confirmation dialog and a useConfirm() helper that returns a Promise<boolean>.
// Usage:
//   const confirm = useConfirm();
//   if (!(await confirm({ title: "Confirm Save?" }))) return;
import { createContext, useCallback, useContext, useState } from "react";
import { AlertTriangle, X, Check } from "lucide-react";

const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null);
  const [resolver, setResolver] = useState(null);

  const ask = useCallback((options) => {
    return new Promise((resolve) => {
      setOpts({
        title: "Are you sure?",
        message: "Please confirm to continue.",
        yesLabel: "Yes",
        noLabel: "No",
        tone: "primary",
        ...options,
      });
      setResolver(() => resolve);
    });
  }, []);

  const close = (val) => {
    if (resolver) resolver(val);
    setOpts(null);
    setResolver(null);
  };

  return (
    <ConfirmCtx.Provider value={ask}>
      {children}
      {opts && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
          data-testid="confirm-dialog">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-start gap-3 p-5 border-b border-black/5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                opts.tone === "danger" ? "bg-rose-100 text-rose-600" : "bg-amber-100 text-amber-600"
              }`}>
                <AlertTriangle size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-extrabold text-base text-black">{opts.title}</h3>
                <p className="text-sm text-black/65 mt-1">{opts.message}</p>
              </div>
              <button onClick={() => close(false)} className="p-1 hover:bg-black/5 rounded">
                <X size={15} />
              </button>
            </div>
            <div className="flex justify-end gap-2 p-4 bg-[#FAFBFB] rounded-b-2xl">
              <button onClick={() => close(false)} data-testid="confirm-no"
                className="px-4 py-2 rounded-xl text-sm font-semibold border border-black/10 hover:bg-white">
                {opts.noLabel}
              </button>
              <button onClick={() => close(true)} data-testid="confirm-yes"
                className={`px-4 py-2 rounded-xl text-sm font-bold text-white inline-flex items-center gap-1.5 ${
                  opts.tone === "danger"
                    ? "bg-gradient-to-r from-rose-500 to-rose-600 hover:opacity-95"
                    : "bg-gradient-to-r from-[#1FA868] to-[#16834D] hover:opacity-95"
                }`}>
                <Check size={13} /> {opts.yesLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Fallback to native confirm if provider not mounted
    return (opts) => Promise.resolve(window.confirm(opts?.message || opts?.title || "Are you sure?"));
  }
  return ctx;
}
